import { useMemo, useState } from 'react';
import type { ResourceBlock } from '@domain/schemas/contract.schema';

import type { CommentThread } from '../utils/threads';

export type CommentFilterType = 'all' | 'resolved' | 'unresolved';
export type CommentSortType = 'newest' | undefined;

interface CommentsFiltersState {
  statusFilter: CommentFilterType;
  sortBy: CommentSortType;
}

interface CommentsFiltersReturn {
  filters: CommentsFiltersState;
  setStatusFilter: (filter: CommentFilterType) => void;
  toggleSortBy: () => void;
  filteredAndSortedThreads: CommentThread[];
  statusOptions: { value: CommentFilterType; label: string }[];
}

const STATUS_OPTIONS = [
  { value: 'all' as const, label: 'All comments' },
  { value: 'unresolved' as const, label: 'Open only' },
  { value: 'resolved' as const, label: 'Resolved only' },
];

const createdAt = (thread: CommentThread) => new Date(thread.root.created_at).getTime();

export const useCommentsFilters = (threads: CommentThread[], blocks: ResourceBlock[]): CommentsFiltersReturn => {
  const [filters, setFilters] = useState<CommentsFiltersState>({ statusFilter: 'all', sortBy: undefined });

  const filteredAndSortedThreads = useMemo(() => {
    const kept = threads.filter(
      (thread) => filters.statusFilter === 'all' || (filters.statusFilter === 'resolved') === thread.isResolved,
    );
    const blockIndex = (thread: CommentThread) => blocks.findIndex((block) => block.id === thread.blockId);

    return [...kept].sort((a, b) =>
      filters.sortBy === 'newest'
        ? createdAt(b) - createdAt(a)
        : blockIndex(a) - blockIndex(b) || createdAt(a) - createdAt(b),
    );
  }, [threads, filters, blocks]);

  const setStatusFilter = (filter: CommentFilterType) => setFilters((prev) => ({ ...prev, statusFilter: filter }));

  const toggleSortBy = () =>
    setFilters((prev) => ({ ...prev, sortBy: prev.sortBy === 'newest' ? undefined : 'newest' }));

  return { filteredAndSortedThreads, filters, setStatusFilter, statusOptions: STATUS_OPTIONS, toggleSortBy };
};
